import { compareVersions } from "@bigbear/shared";
import { Link, useParams } from "react-router-dom";

import { downloadUrlsFor } from "../api/updates";
import { ReleaseNotes } from "../components/ReleaseNotes";
import { useRelease, useReleases } from "../useReleases";

export function ReleaseDetail() {
  const { version = "" } = useParams();
  const { release, settled } = useRelease(version);
  const releases = useReleases();

  if (!release) {
    // A version the build-time bundle doesn't have might still be one the API
    // knows about — published after the last portal build. Saying "no such
    // release" before that answer arrives would be wrong for exactly the case
    // this page most needs to get right.
    if (!settled) {
      return <p className="muted">Loading release notes…</p>;
    }

    return (
      <div className="empty">
        <h2>No such release</h2>
        <p className="muted">
          Nothing published matches version <span className="mono">{version}</span>.
        </p>
        <p>
          <Link to="/releases">Back to all releases</Link>
        </p>
      </div>
    );
  }

  const { nsisUrl, msiUrl } = downloadUrlsFor(release.version);
  // Compared rather than matched for equality: this page can be showing a
  // release the list doesn't have yet — one published after the last portal
  // build, fetched by version — and equality would then call the newest
  // release "superseded" by an older one.
  const newest = releases[0]?.version;
  const supersededBy = newest && compareVersions(newest, release.version) > 0 ? newest : null;
  const isLatest = !supersededBy;

  return (
    <>
      <p className="crumb">
        <Link to="/releases">Releases</Link> › v{release.version}
      </p>

      <section className="panel release-entry">
        <div className="release-entry__header">
          <h1>
            v{release.version} {isLatest && <span className="badge badge--healthy">Latest</span>}
          </h1>
          <span className="muted">{release.date}</span>
        </div>
        <p className="release-entry__headline">{release.headline}</p>

        {supersededBy && (
          <p className="muted">
            Superseded by <Link to={`/releases/${supersededBy}`}>v{supersededBy}</Link>.
          </p>
        )}

        <ReleaseNotes body={release.body} />

        <div className="download__actions">
          <a className="button button--primary" href={nsisUrl}>
            Download installer (.exe)
          </a>
          <a className="button" href={msiUrl}>
            Download MSI
          </a>
        </div>
      </section>
    </>
  );
}
